import { FormikConfig, useFormik } from 'formik';
import React from 'react';

import * as ManifestUI from '@project44/manifest-ds';

import {
  IFormClient,
  IFormClientConfigParams,
  IUIFormClient,
} from '../interface';
import { HTMLFormProperty } from './HTMLFormProperty';

type StringKeys<T> = Extract<keyof T, string>;

export abstract class UIFormClient<T extends { [k in keyof T]: string }>
  implements IUIFormClient<T>
{
  public abstract properties: IUIFormClient<T>['properties'];
  public abstract validationSchema: IUIFormClient<T>['validationSchema'];
  public formikConfig: IUIFormClient<T>['formikConfig'] = {};

  public readonly configureFormClient = (
    onSubmitHandler: FormikConfig<T>['onSubmit'],
  ): IFormClientConfigParams<T> => {
    return {
      ...this.formikConfig,
      initialValues: this.composeInitialValues(),
      onSubmit: onSubmitHandler,
      validationSchema: this.validationSchema,
    };
  };

  public readonly composeRendererValues = (): {
    [K: string]: HTMLFormProperty;
  } => {
    const rendererValues = {} as {
      [K: string]: HTMLFormProperty;
    };

    for (const [
      formPropertyName,
      formPropertyConfig,
    ] of Object.entries<HTMLFormProperty>(this.properties)) {
      if (formPropertyConfig.isDisplayable) {
        rendererValues[formPropertyName] = formPropertyConfig;
      }
    }

    return rendererValues;
  };

  public get renderForm(): React.FC<{
    onSubmitHandler: FormikConfig<T>['onSubmit'];
    isLoading?: boolean;
  }> {
    return ({ onSubmitHandler, isLoading }) => {
      const formClient = useFormik<T>(
        this.configureFormClient(onSubmitHandler),
      ) as unknown as IFormClient;

      const isLayoutHorizontal = false;
      const formControlGridColumns = isLayoutHorizontal ? 4 : 12;
      const formComponentGridColumns = isLayoutHorizontal ? 8 : 12;

      return (
        <form data-testid={'UIFormClient'} onSubmit={formClient.submitForm}>
          {Object.entries(this.composeRendererValues()).map(
            ([formKey, HTMLFormProperty]) => {
              return (
                <ManifestUI.Grid
                  key={formKey}
                  role={'presentation'}
                  aria-label={`form-field-container-${formKey}`}
                  columns="repeat(12, 1fr)"
                  gap="x-small"
                  rows={'none'}
                  css={{
                    mb: ManifestUI.designTokens.spaceMedium,
                  }}
                >
                  {
                    <ManifestUI.GridItem
                      role={'presentation'}
                      aria-label={`form-control-${formKey}`}
                      column={formControlGridColumns}
                      css={{
                        display: 'flex',
                        alignItems: 'top',
                      }}
                    >
                      <HTMLFormProperty.renderFormControlComponent
                        propertyName={formKey}
                        formClient={formClient}
                        isLoading={isLoading}
                      />
                    </ManifestUI.GridItem>
                  }
                  <ManifestUI.GridItem column={formComponentGridColumns}>
                    <HTMLFormProperty.renderFormTypeComponent
                      propertyName={formKey}
                      formClient={formClient}
                      isLoading={isLoading}
                    />
                  </ManifestUI.GridItem>
                </ManifestUI.Grid>
              );
            },
          )}
        </form>
      );
    };
  }

  public renderHTMLFormProperty: React.FC<{
    formClient: IFormClient;
    propertyName: StringKeys<T>;
    manifestUIBoxCss?: ManifestUI.CSS;
    isLoading?: boolean;
    withFormControl?: boolean;
    label?: string;
  }> = ({
    withFormControl = true,
    isLoading,
    manifestUIBoxCss,
    formClient,
    propertyName,
    label,
  }) => {
    const HTMLFormProperty = this.composeRendererValues()[propertyName];

    return HTMLFormProperty ? (
      <ManifestUI.Box css={manifestUIBoxCss}>
        {withFormControl && (
          <HTMLFormProperty.renderFormControlComponent
            label={label}
            propertyName={propertyName}
            formClient={formClient}
            isLoading={isLoading}
          />
        )}
        <HTMLFormProperty.renderFormTypeComponent
          propertyName={propertyName}
          formClient={formClient}
          isLoading={isLoading}
        />
      </ManifestUI.Box>
    ) : null;
  };

  private readonly composeInitialValues =
    (): IFormClientConfigParams<T>['initialValues'] => {
      const initialValues: {
        [K: string]: string;
      } = {};

      for (const [
        formPropertyName,
        formPropertyConfig,
      ] of Object.entries<HTMLFormProperty>(this.properties)) {
        initialValues[formPropertyName] = formPropertyConfig.initialValue;
      }

      return initialValues as IFormClientConfigParams<T>['initialValues'];
    };
}
